import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Task } from 'ember-concurrency';
import PowerSelect from 'ember-power-select/components/power-select';
import ConfirmDirtyTransition from 'garaje/components/confirm-dirty-transition';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import type LocationModel from 'garaje/models/location';
import type Location from 'garaje/models/location';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import { formatMinutesAsTime, getMinutesInDayFromTime, TIMES } from 'garaje/utils/format-minutes-as-time';

interface AutoSignOutTimeSignature {
  Args: {
    location: LocationModel;
    saveTask: Task<void, []>;
    updateAutoSignOutAtMinutesSinceMidnight: (mins: number | null) => void;
  };
}

const DEFAULT_TIME = '12:00 am';

export default class AutoSignOutTime extends Component<AutoSignOutTimeSignature> {
  @service declare workplaceMetrics: WorkplaceMetricsService;

  @tracked isOpen = false;
  @tracked showUnsavedEditsWarning = false;
  times = TIMES;

  get isEnabled(): boolean {
    const { location: currentLocation } = this.args;
    const autoSignOutAtMinutesSinceMidnight = <[number, number]>(
      currentLocation.changedAttributes()['autoSignOutAtMinutesSinceMidnight']
    );

    if (
      <boolean>(<unknown>currentLocation.hasDirtyAttributes) &&
      autoSignOutAtMinutesSinceMidnight &&
      autoSignOutAtMinutesSinceMidnight.length
    ) {
      const [original, _changed] = autoSignOutAtMinutesSinceMidnight;
      return original !== null;
    } else {
      return currentLocation.autoSignOutAtMinutesSinceMidnight !== null;
    }
  }

  get time(): string {
    const { location: currentLocation } = this.args;
    const minutesInDay = currentLocation.autoSignOutAtMinutesSinceMidnight;
    return formatMinutesAsTime(minutesInDay || 0);
  }

  @action
  onContinue(): void {
    const { location: currentLocation } = this.args;
    if (<boolean>(<unknown>currentLocation.hasDirtyAttributes)) {
      currentLocation.rollbackAttributes();
    }
    this.showUnsavedEditsWarning = false;
    this.isOpen = false;
  }

  @action
  cancel(): void {
    const { location: currentLocation } = this.args;
    const autoSignOutAtMinutesSinceMidnight = <
      [Location['autoSignOutAtMinutesSinceMidnight'], Location['autoSignOutAtMinutesSinceMidnight']]
    >currentLocation.changedAttributes()['autoSignOutAtMinutesSinceMidnight'];
    if (!autoSignOutAtMinutesSinceMidnight || !autoSignOutAtMinutesSinceMidnight.length) {
      return this.onContinue();
    }
    const [original, _changed] = autoSignOutAtMinutesSinceMidnight;
    if (original === null) {
      // Not enabled yet
      this.onContinue();
    } else {
      this.showUnsavedEditsWarning = true;
    }
  }

  @action
  edit(): void {
    this.workplaceMetrics.trackEvent('LOCATION_SETTINGS_AUTO_SIGN_OUT_EDIT_BUTTON_CLICKED');
    this.isOpen = true;
  }

  @action
  enable(): void {
    this.setTime(DEFAULT_TIME);
    this.isOpen = true;
  }

  @action
  async disable(): Promise<void> {
    this.args.updateAutoSignOutAtMinutesSinceMidnight(null);
    await this.args.saveTask.perform();
    this.isOpen = false;
  }

  @action
  async save(): Promise<void> {
    this.workplaceMetrics.trackEvent('LOCATION_SETTINGS_AUTO_SIGN_OUT_SETTINGS_SAVE_BUTTON_CLICKED');
    await this.args.saveTask.perform();
    this.isOpen = false;
  }

  @action
  setTime(time: string): void {
    const minutesInDay = getMinutesInDayFromTime(time);
    this.args.updateAutoSignOutAtMinutesSinceMidnight(minutesInDay);
  }

  @action
  cancelUnsavedEdits(): void {
    this.showUnsavedEditsWarning = false;
  }

  <template>
    <SettingsPanel @isOpen={{this.isOpen}} data-test-auto-sign-out as |sp|>
      {{#let sp.header sp.body sp.footer as |PanelHeader PanelBody PanelFooter|}}
        <PanelHeader
          @iconUri="/assets/images/features/auto-signout.svg"
          @title="Automatic sign-out"
          @onCancel={{this.cancel}}
          as |header|
        >
          <header.description>
            {{#if this.isEnabled}}
              Automatically sign out employees and visitors at
              {{this.time}}.
            {{else}}
              Choose when to automatically sign out all visitors.
            {{/if}}
          </header.description>
          <header.controls>
            {{#if sp.isOpen}}
              <button
                data-test-save-auto-sign-out
                type="button"
                class="btn btn--small btn--brand"
                disabled={{@saveTask.isRunning}}
                {{on "click" this.save}}
              >
                {{if @saveTask.isRunning "Saving..." "Save"}}
              </button>
            {{else if this.isEnabled}}
              <button
                data-test-edit-auto-sign-out
                type="button"
                {{on "click" this.edit}}
                class="btn btn--small btn--brand"
              >
                Edit
              </button>
            {{else}}
              <button
                data-test-enable-auto-sign-out
                type="button"
                {{on "click" this.enable}}
                class="btn btn--small btn--brand"
              >
                Enable
              </button>
            {{/if}}
          </header.controls>
        </PanelHeader>
        <PanelBody as |PanelContent|>
          <PanelContent @style="inset" class="mt-8">
            <div data-test-auto-sign-out-at-time class="max-w-xs" style="max-height: 11rem;">
              <label id="sign-out-time">Sign visitors out at</label>
              <PowerSelect
                @ariaDescribedBy="sign-out-time"
                @searchEnabled={{false}}
                @selected={{this.time}}
                @options={{this.times}}
                @onChange={{this.setTime}}
                as |time|
              >
                {{time}}
              </PowerSelect>
            </div>
          </PanelContent>
          {{#if this.isEnabled}}
            <PanelFooter>
              <button
                data-test-disable-auto-sign-out
                type="button"
                class="btn btn--small btn--outlineFill"
                disabled={{@saveTask.isRunning}}
                {{on "click" this.disable}}
              >
                {{if @saveTask.isRunning "Disabling..." "Disable"}}
              </button>
            </PanelFooter>
          {{/if}}
        </PanelBody>
      {{/let}}
    </SettingsPanel>
    {{#if this.showUnsavedEditsWarning}}
      <ConfirmDirtyTransition
        @headerMessage="You have unsaved changes"
        @bodyMessage="You will lose any unsaved changes. Are you sure you want to continue?"
        @confirm={{this.onContinue}}
        @cancel={{this.cancelUnsavedEdits}}
      />
    {{/if}}
  </template>
}
